// STAGING-ONLY FRAME PROBE (2026-10-13, TEMPORARY: remove once playback smoothness is settled). Chris: the bar work made playback "staggered and laggy", and after the first fix it was
// "still a little laggy". I can measure the scrub bar alone, but not the bubbles and the bar together in the real app (no login), so the app reports its own frame health on
// the staging build stamp, where Chris already screenshots it. Nothing is sent anywhere. Never runs off staging.
//
// What it records over a sliding window (WINDOW_MS): the gap between animation frames, long main-thread tasks (Chrome's Long Tasks API), how often the scrub bar redrew and
// how long each redraw took, the page's DOM size and the JS heap. The pure part (summarising a list of frame gaps) is tested.
import { useLayoutEffect } from "react";

export const WINDOW_MS = 5000;
const SLOW_FRAME_MS = 25;

export interface FrameSummary {
  frames: number;
  fps: number;
  slow: number; // frames that took longer than SLOW_FRAME_MS
  worstMs: number;
  p95Ms: number;
}

// gaps: milliseconds between consecutive animation frames, over a window of `windowMs`
export const summariseFrames = (gaps: number[], windowMs: number): FrameSummary => {
  const clean = gaps.filter((gap) => Number.isFinite(gap) && gap > 0);
  if (clean.length === 0) {
    return { frames: 0, fps: 0, slow: 0, worstMs: 0, p95Ms: 0 };
  }
  const sorted = [...clean].sort((a, b) => a - b);

  return {
    frames: clean.length,
    fps: Math.round((clean.length / windowMs) * 1000),
    slow: clean.filter((gap) => gap > SLOW_FRAME_MS).length,
    worstMs: Math.round(sorted[sorted.length - 1]),
    p95Ms: Math.round(sorted[Math.min(sorted.length - 1, Math.floor(sorted.length * 0.95))]),
  };
};

// Anything that can say what the app was doing (the playback clock, how many visitors are onsite) registers here, so a screenshot says under what load its numbers were taken.
const contextProviders = new Map<string, () => string>();
export const setPerfContext = (key: string, fn: (() => string) | null) => {
  if (fn) {
    contextProviders.set(key, fn);
  } else {
    contextProviders.delete(key);
  }
};
const contextText = (): string =>
  Array.from(contextProviders.values())
    .map((fn) => {
      try {
        return fn();
      } catch (e) {
        return "";
      }
    })
    .filter(Boolean)
    .join(", ");

// The worst full window of the run so far, by share of slow frames (a window needs at least MIN_FRAMES_FOR_WORST frames to count, so a stalled tab cannot win with three samples).
export const MIN_FRAMES_FOR_WORST = 100;
export interface WorstWindow {
  ratio: number;
  summary: FrameSummary;
  context: string;
  cost: string; // what the app was spending its time on during that window
}
export const betterWorst = (previous: WorstWindow | null, summary: FrameSummary, context: string, cost = ""): WorstWindow | null => {
  if (summary.frames < MIN_FRAMES_FOR_WORST) {
    return previous;
  }
  const ratio = summary.slow / summary.frames;

  return !previous || ratio > previous.ratio ? { ratio, summary, context, cost } : previous;
};
let worst: WorstWindow | null = null;

export const isStagingHost = (hostname: string): boolean => /staging/i.test(hostname);

// `?nobar=1` on a staging URL removes the scrub bar entirely: an A/B switch, so "is it the bar?" is answered by reloading with and without it.
export const scrubBarSwitchedOff = (hostname: string, search: string): boolean => isStagingHost(hostname) && /[?&]nobar=1(&|$)/.test(search);


// SLOW-FRAME ATTRIBUTION. The Long Animation Frames API (Chrome 123+) says, for a frame that took 50ms or more: how long it was in total, how much of that was JavaScript, how much was
// style/layout/paint, and which script ran longest (what called it, which file, and the character position in it, which the build's source map turns back into a function). The
// three worst of the run are kept and shown on the stamp. That is what separates "something in our code is slow" from "the browser is slow to paint".
export interface SlowFrame {
  totalMs: number;
  scriptMs: number;
  renderMs: number; // style, layout and paint after the scripts
  invoker: string;
  where: string;
}
interface LoafScript {
  duration: number;
  invoker?: string;
  invokerType?: string;
  sourceURL?: string;
  sourceFunctionName?: string;
  sourceCharPosition?: number;
}
interface LoafEntry {
  startTime: number;
  duration: number;
  renderStart?: number;
  styleAndLayoutStart?: number;
  scripts?: LoafScript[];
}
export const describeFrame = (entry: LoafEntry): SlowFrame => {
  const scripts = entry.scripts || [];
  const top = scripts.reduce<LoafScript | null>((best, script) => (!best || script.duration > best.duration ? script : best), null);
  const scriptMs = scripts.reduce((sum, script) => sum + script.duration, 0);
  const end = entry.startTime + entry.duration;
  const renderMs = entry.renderStart ? Math.max(0, end - entry.renderStart) : 0;
  const file = (top?.sourceURL || "").split("/").pop() || "?";

  return {
    totalMs: Math.round(entry.duration),
    scriptMs: Math.round(scriptMs),
    renderMs: Math.round(renderMs),
    // invokerType says what kind of call it was; invoker names it: for an event listener it is the element and event ("CIRCLE.onmouseover"), for a timer or callback the API
    invoker: top ? `${top.invokerType || "?"}${top.invoker ? ` ${top.invoker}` : ""}${top.sourceFunctionName ? ` ${top.sourceFunctionName}` : ""}` : "no script",
    where: top ? `${file}:${top.sourceCharPosition ?? "?"}` : "-",
  };
};
export const WORST_FRAMES_KEPT = 3;
export const keepWorstFrames = (kept: SlowFrame[], frame: SlowFrame): SlowFrame[] => [...kept, frame].sort((a, b) => b.totalMs - a.totalMs).slice(0, WORST_FRAMES_KEPT);
let worstFrames: SlowFrame[] = [];
const frameText = (): string =>
  worstFrames.length ? ` · SLOWEST FRAMES: ${worstFrames.map((f) => `${f.totalMs}ms (js ${f.scriptMs}, render ${f.renderMs}) [${f.invoker} @ ${f.where}]`).join(" | ")}` : "";

// COST SPANS. Code that runs on the hot path reports how long a piece of work took: a React component's render (inclusive of its children), one playback action, one tick
// listener. The window's totals by name say WHERE the main thread's time went, which a frame count cannot. Reported as "name count x total ms (max)".
export interface Span {
  at: number;
  name: string;
  ms: number;
}
export const summariseSpans = (spans: Array<{ name: string; ms: number }>, top = 6): string => {
  const byName = new Map<string, { n: number; total: number; max: number }>();
  spans.forEach((span) => {
    const entry = byName.get(span.name) || { n: 0, total: 0, max: 0 };
    entry.n += 1;
    entry.total += span.ms;
    entry.max = Math.max(entry.max, span.ms);
    byName.set(span.name, entry);
  });

  return Array.from(byName.entries())
    .sort((a, b) => b[1].total - a[1].total)
    .slice(0, top)
    .map(([name, e]) => `${name} ${e.n}x ${Math.round(e.total)}ms (max ${Math.round(e.max)})`)
    .join(" | ");
};
const spansLog: Span[] = [];
// cheap and safe to call anywhere: it does nothing until the probe has started (which only happens on a staging host)
export const noteSpan = (name: string, ms: number) => {
  if (started) {
    spansLog.push({ at: performance.now(), name, ms });
  }
};
// React: how long a component took to render, children included (render start to the layout-effect of the same commit). Call as the FIRST line of the component.
// The probe exists only in a staging build: REACT_APP_BUILD_ID is set for those (it is what the build stamp shows) and never for a production build, so production's render path has no
// probe hook at all. A module constant, so the same hook calls run on every render of a given build (the rules of hooks hold).
export const PROBE_BUILD = !!process.env.REACT_APP_BUILD_ID;
export const usePerfRender = (name: string) => {
  if (!PROBE_BUILD) {
    return;
  }
  const t0 = started ? performance.now() : 0;
  useLayoutEffect(() => {
    if (t0) {
      noteSpan(`render:${name}`, performance.now() - t0);
    }
  });
};

let started = false;
const frames: Array<{ at: number; gap: number }> = [];
const longTasks: Array<{ at: number; ms: number }> = [];
const barRedraws: Array<{ at: number; ms: number }> = [];
let lastFrame = 0;

const prune = (list: Array<{ at: number }>, now: number) => {
  while (list.length && now - list[0].at > WINDOW_MS) {
    list.shift();
  }
};

// called by the scrub bar after each full redraw
export const noteScrubRender = (ms: number) => {
  if (started) {
    barRedraws.push({ at: performance.now(), ms });
  }
};

const start = () => {
  started = true;
  const onFrame = (ts: number) => {
    if (lastFrame) {
      frames.push({ at: ts, gap: ts - lastFrame });
    }
    lastFrame = ts;
    prune(frames, ts);
    requestAnimationFrame(onFrame);
  };
  requestAnimationFrame(onFrame);
  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => longTasks.push({ at: performance.now(), ms: entry.duration }));
    }).observe({ entryTypes: ["longtask"] });
  } catch (e) {
    // not every browser has the Long Tasks API; the frame gaps still say it
  }
  try {
    new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        worstFrames = keepWorstFrames(worstFrames, describeFrame(entry as unknown as LoafEntry));
      });
    }).observe({ type: "long-animation-frame", buffered: true } as PerformanceObserverInit);
  } catch (e) {
    // an older Chrome has no Long Animation Frames; the numbers above still stand
  }
};

// The line appended to the staging build stamp. Starts itself on first use, and only on a staging host.
export const perfProbeText = (): string => {
  if (typeof window === "undefined" || !isStagingHost(window.location.hostname)) {
    return "";
  }
  if (!started) {
    start();

    return "perf: starting";
  }
  const now = performance.now();
  prune(frames, now);
  prune(longTasks, now);
  prune(barRedraws, now);
  prune(spansLog, now);
  const f = summariseFrames(frames.map((entry) => entry.gap), WINDOW_MS);
  const context = contextText();
  const cost = summariseSpans(spansLog);
  worst = betterWorst(worst, f, context, cost);
  const longest = longTasks.reduce((max, task) => Math.max(max, task.ms), 0);
  const redrawMs = barRedraws.reduce((sum, redraw) => sum + redraw.ms, 0);
  const heap = (performance as any).memory?.usedJSHeapSize;
  const heapText = heap ? ` · heap ${Math.round(heap / 1048576)}MB` : "";
  const worstText = worst ? ` · WORST 5s SO FAR: ${worst.summary.slow}/${worst.summary.frames} slow (${Math.round(worst.ratio * 100)}%), p95 ${worst.summary.p95Ms}ms, worst frame ${worst.summary.worstMs}ms, at ${worst.context} · COST THEN: ${worst.cost || "-"}` : "";

  return `PERF now [${context}]: ${f.fps} fps, ${f.slow}/${f.frames} slow (>${SLOW_FRAME_MS}ms), p95 ${f.p95Ms}ms, worst ${f.worstMs}ms · long tasks ${longTasks.length} (longest ${Math.round(longest)}ms) · bar redraws ${barRedraws.length} (${redrawMs.toFixed(0)}ms) · DOM ${document.getElementsByTagName("*").length}${heapText}${worstText}${frameText()} · COST NOW: ${cost || "-"}`;
};
